import {
  DatabaseOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Input,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useMemo, useState } from "react";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { RedisKey } from "../types";
import { apiClient } from "../utils/api";

const { Title, Text } = Typography;
const { Search } = Input;

const RedisPage: React.FC = () => {
  const [searchText, setSearchText] = useState<string>("");
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<RedisKey | null>(null);
  const [keyValue, setKeyValue] = useState<string>("");
  const queryClient = useQueryClient();

  const { data: redisKeys = [], isLoading } = useQuery<RedisKey[]>({
    queryKey: ["redisKeys"],
    queryFn: () => apiClient.getRedisKeys() as Promise<RedisKey[]>,
  });

  const filteredKeys = useMemo(() => {
    if (!searchText) return redisKeys;
    return redisKeys.filter((key) =>
      key.key.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [redisKeys, searchText]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["redisKeys"] });
  };

  const handleViewKey = async (key: RedisKey) => {
    try {
      setSelectedKey(key);
      setDetailModalVisible(true);
      setKeyValue("Loading...");

      const keyDetails = await apiClient.getRedisKey(key.key);
      let displayValue = keyDetails.value || "";

      try {
        // Try to format JSON if it looks like one
        if (
          displayValue &&
          (displayValue.trim().startsWith("{") ||
            displayValue.trim().startsWith("["))
        ) {
          const parsed = JSON.parse(displayValue);
          displayValue = JSON.stringify(parsed, null, 2);
        }
      } catch (e) {
        // Not valid JSON, keep original value
      }

      setKeyValue(displayValue);
    } catch (error) {
      message.error("Failed to fetch key value");
      console.error("Failed to fetch key value:", error);
      setKeyValue("Error loading value");
    }
  };

  const handleDeleteKey = async (key: RedisKey) => {
    Modal.confirm({
      title: "Delete Key",
      content: `Are you sure you want to delete the key "${key.key}"?`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Assuming API client has a method to delete key
          // await apiClient.deleteRedisKey(key.key);
          message.success("Key deleted successfully");
          handleRefresh();
        } catch (error) {
          message.error("Failed to delete key");
          console.error("Failed to delete key:", error);
        }
      },
    });
  };

  const getTypeColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      string: "blue",
      hash: "green",
      list: "orange",
      set: "purple",
      zset: "red",
    };
    return colorMap[type] || "default";
  };

  const formatSize = (bytes: number) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return "-";
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatTTL = (ttl: number | null) => {
    if (!ttl || ttl === -1) return "No expiry";
    if (ttl < 60) return `${ttl}s`;
    if (ttl < 3600) return `${Math.floor(ttl / 60)}m ${ttl % 60}s`;
    if (ttl < 86400)
      return `${Math.floor(ttl / 3600)}h ${Math.floor((ttl % 3600) / 60)}m`;
    return `${Math.floor(ttl / 86400)}d ${Math.floor((ttl % 86400) / 3600)}h`;
  };

  const redisKeyColumns: ColumnsType<RedisKey> = [
    {
      title: "Key",
      dataIndex: "key",
      key: "key",
      render: (key: string) => (
        <Text code style={{ wordBreak: "break-all" }}>
          {key}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type.toUpperCase()}</Tag>
      ),
      filters: [
        { text: "STRING", value: "string" },
        { text: "HASH", value: "hash" },
        { text: "LIST", value: "list" },
        { text: "SET", value: "set" },
        { text: "ZSET", value: "zset" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
      render: (size: number) => formatSize(size),
      sorter: (a, b) => a.size - b.size,
    },
    {
      title: "TTL",
      dataIndex: "ttl",
      key: "ttl",
      width: 120,
      render: (ttl: number | null) => (
        <Text type={ttl && ttl > 0 && ttl < 3600 ? "warning" : undefined}>
          {formatTTL(ttl)}
        </Text>
      ),
      sorter: (a, b) => (a.ttl || 0) - (b.ttl || 0),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View key">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewKey(record)}
            />
          </Tooltip>
          <Tooltip title="Delete key">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteKey(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout
      title="Redis Management"
      extraActions={
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={isLoading}
        >
          Refresh
        </Button>
      }
    >
      <BreadcrumbNav />

      <Card
        title={
          <span>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            Redis Management
          </span>
        }
      >
        <div className="responsive-filters" style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search keys..."
            allowClear
            onSearch={(value) => setSearchText(value)}
            onChange={(e) => !e.target.value && setSearchText("")}
            style={{ width: 250 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            Refresh
          </Button>
        </div>
        <Table
          columns={redisKeyColumns}
          dataSource={filteredKeys}
          rowKey="key"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} keys`,
          }}
          scroll={{ x: 600 }}
        />
      </Card>

      <Modal
        title={
          <span>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            Key Details: {selectedKey?.key}
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedKey && (
          <div>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <Text strong>Key:</Text>
                <Text code style={{ marginLeft: 8, wordBreak: "break-all" }}>
                  {selectedKey.key}
                </Text>
              </div>
              <div>
                <Text strong>Type:</Text>
                <Tag
                  color={getTypeColor(selectedKey.type)}
                  style={{ marginLeft: 8 }}
                >
                  {selectedKey.type.toUpperCase()}
                </Tag>
              </div>
              <div>
                <Text strong>Size:</Text>
                <Text style={{ marginLeft: 8 }}>
                  {formatSize(selectedKey.size)}
                </Text>
              </div>
              <div>
                <Text strong>TTL:</Text>
                <Text style={{ marginLeft: 8 }}>
                  {formatTTL(selectedKey.ttl)}
                </Text>
              </div>
              <div>
                <Text strong>Value:</Text>
                <pre
                  style={{
                    marginTop: 8,
                    padding: 12,
                    background: "#f5f5f5",
                    borderRadius: 4,
                    maxHeight: 300,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {keyValue || "Loading..."}
                </pre>
              </div>
            </Space>
          </div>
        )}
      </Modal>
    </AppLayout>
  );
};

export default RedisPage;
